// src/components/ServiceQuoteForm.js
// Unified quote calculator: a customer can select one, several, or all 4 service categories and
// get one combined total (line discounts + order-level volume discount + $90 minimum booking
// check). Replaces the old Residential/Commercial/DeepCleaning quote forms, which each priced a
// single category tier and disagreed with each other and with the Booking form.
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import QuoteToast from './QuoteToast';
import Spinner from './Spinner';
import ServiceCategoryFields from './ServiceCategoryFields';
import apiClient from '../utils/apiClient';
import { CATEGORIES, MINIMUM_BOOKING, calculateQuote } from '../utils/calculateTotal.js';
import useSeo, { buildBreadcrumbs } from '../hooks/useSeo.js';
import './ServiceQuoteForm.css';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9+\-\s()]{6,20}$/;

const CATEGORY_IMAGES = {
  carpet: { src: require('../assets/carpet&rug.jpg'), alt: 'Residential carpet and upholstery cleaning in a Perth home' },
  upholstery: { src: require('../assets/upholstery&mattress.jpg'), alt: 'Upholstery and couch cleaning in a Perth home' },
  mattress: { src: require('../assets/mattress-cleaning.jpg'), alt: 'Professional mattress cleaning in a Perth home' },
  curtains: { src: require('../assets/curtains&blinds.webp'), alt: 'Curtain and blinds cleaning in a Perth home' },
  window: { src: require('../assets/windows-glass-cleaning.jpg'), alt: 'Professional window and glass cleaning in Perth' },
};

const emptyDetails = () => {
  const details = {};
  CATEGORIES.forEach((category) => {
    details[category.key] = {};
    category.fields.forEach((field) => {
      details[category.key][field.key] = 0;
    });
  });
  return details;
};

// seoEnabled: false when embedded elsewhere (the Homepage's quote calculator) so it doesn't
// override that page's own title/canonical/structured-data.
const ServiceQuoteForm = ({ seoEnabled = true }) => {
  useSeo({
    title: 'Get a Free Cleaning Quote – Carpet, Upholstery, Curtains & Windows',
    description:
      'Get an instant quote for carpet & rug, upholstery & mattress, curtains & blinds, or window glass cleaning in Perth, Western Australia.',
    path: '/get-quote',
    structuredData: buildBreadcrumbs([
      { name: 'Home', path: '/' },
      { name: 'Get a Quote', path: '/get-quote' },
    ]),
    enabled: seoEnabled,
  });

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [contactErrors, setContactErrors] = useState({});

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [details, setDetails] = useState(emptyDetails);
  const [categoryError, setCategoryError] = useState('');
  const [quoteResult, setQuoteResult] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [leadToast, setLeadToast] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const firstInputRef = useRef(null);
  const containerRef = useRef(null);
  const preselectedCategory = location.state?.preselectedCategory;
  const [showToast, setShowToast] = useState(Boolean(preselectedCategory));

  useEffect(() => {
    if (preselectedCategory && CATEGORIES.some((c) => c.key === preselectedCategory)) {
      setSelectedCategories((prev) => (prev.includes(preselectedCategory) ? prev : [...prev, preselectedCategory]));
      setShowToast(true);
    }

    if (firstInputRef.current) {
      // preventScroll matters here: this component mounts as soon as the Homepage loads (it's
      // the quote calculator), and an unguarded focus() would yank the page's scroll position
      // down to this input every single time — before the visitor even sees the hero.
      firstInputRef.current.focus({ preventScroll: true });
    }
    if (containerRef.current) {
      containerRef.current.setAttribute('tabindex', '-1');
      containerRef.current.focus({ preventScroll: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCategory]);

  const toggleCategory = (key) => {
    setCategoryError('');
    setSelectedCategories((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const toggleSelectAll = () => {
    setCategoryError('');
    setSelectedCategories((prev) => (prev.length === CATEGORIES.length ? [] : CATEGORIES.map((c) => c.key)));
  };

  const handleFieldChange = (categoryKey, fieldKey, value) => {
    setDetails((prev) => ({ ...prev, [categoryKey]: { ...prev[categoryKey], [fieldKey]: value } }));
  };

  // Prefers real browser back (so the visitor lands exactly where they were on Services, scroll
  // position included) and only falls back to a fresh navigation if there's no history to go
  // back to (e.g. this page was opened directly via URL).
  const backToServices = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/services');
    }
  };

  const backToBook = () => {
    const activeDetails = {};
    selectedCategories.forEach((key) => {
      activeDetails[key] = details[key];
    });
    navigate('/', {
      state: {
        scrollToBooking: true,
        prefillContact: { fullName, email, phone },
        prefillCategories: selectedCategories,
        prefillDetails: activeDetails,
      },
    });
  };

  const validateContact = () => {
    const errors = {};
    if (!fullName.trim()) errors.fullName = 'Full name is required.';
    if (!email.trim() || !EMAIL_REGEX.test(email.trim())) errors.email = 'Enter a valid email address.';
    if (!phone.trim() || !PHONE_REGEX.test(phone.trim())) errors.phone = 'Enter a valid phone number.';
    setContactErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCalculate = async () => {
    const contactValid = validateContact();
    if (selectedCategories.length === 0) {
      setCategoryError('Select at least one service to get a quote.');
      setHasCalculated(false);
      return;
    }
    if (!contactValid) {
      setHasCalculated(false);
      return;
    }

    const activeDetails = {};
    selectedCategories.forEach((key) => {
      activeDetails[key] = details[key];
    });

    const result = calculateQuote(activeDetails);
    setQuoteResult(result);
    setHasCalculated(true);

    setCalculating(true);
    try {
      await apiClient.post('/quote-leads', {
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        quoteCategory: selectedCategories,
        details: activeDetails,
        quotedTotal: result.total,
        sourcePage: 'get-quote',
      });
      setLeadToast({ title: 'Quote saved', message: "We've saved your quote — our team can follow up if you'd like." });
    } catch (err) {
      setLeadToast({
        variant: 'error',
        title: "We couldn't save your quote",
        message:
          err.response?.status === 429
            ? "You've submitted a few requests in a short time — your total is ready below, but please wait a few minutes and recalculate so our team can follow up."
            : 'Your total is ready below, but please try again in a moment so our team can follow up with you.',
      });
    } finally {
      setCalculating(false);
    }
  };

  const preselectedLabel = CATEGORIES.find((c) => c.key === preselectedCategory)?.label;

  const formContent = (
    <div className="quote-form" ref={containerRef}>
      {preselectedCategory && showToast && (
        <QuoteToast
          title="Service selected"
          message={`We've pre-selected ${preselectedLabel} for you — feel free to add more services below.`}
          onClose={() => setShowToast(false)}
        />
      )}

      {leadToast && (
        <QuoteToast
          variant={leadToast.variant}
          title={leadToast.title}
          message={leadToast.message}
          onClose={() => setLeadToast(null)}
        />
      )}

      <h2>Get Your Free Quote</h2>

      <div className="form-group">
        <label htmlFor="fullName">Full Name</label>
        <input
          ref={firstInputRef}
          type="text"
          id="fullName"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Enter your full name"
        />
        {contactErrors.fullName && <p className="field-error">{contactErrors.fullName}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
        />
        {contactErrors.email && <p className="field-error">{contactErrors.email}</p>}
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone Number</label>
        <input
          type="tel"
          id="phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Enter your phone number"
        />
        {contactErrors.phone && <p className="field-error">{contactErrors.phone}</p>}
      </div>

      <div className="form-group">
        <label>Which service(s) would you like a quote for?</label>
        <div className="quote-tabs" role="group" aria-label="Select the services you'd like a quote for">
          <button
            type="button"
            className={`quote-tab-btn ${selectedCategories.length === CATEGORIES.length ? 'active' : ''}`}
            onClick={toggleSelectAll}
          >
            Select all
          </button>
          {CATEGORIES.map((category) => (
            <button
              key={category.key}
              type="button"
              className={`quote-tab-btn ${selectedCategories.includes(category.key) ? 'active' : ''}`}
              aria-pressed={selectedCategories.includes(category.key)}
              onClick={() => toggleCategory(category.key)}
            >
              {category.label}
            </button>
          ))}
        </div>
        {categoryError && <p className="field-error">{categoryError}</p>}
      </div>

      {CATEGORIES.filter((category) => selectedCategories.includes(category.key)).map((category) => (
        <ServiceCategoryFields
          key={category.key}
          category={category}
          values={details[category.key]}
          onChange={handleFieldChange}
        />
      ))}

      <button className="calculate-btn" onClick={handleCalculate} disabled={calculating}>
        {calculating ? (
          <>
            <Spinner size="sm" inline /> Calculating…
          </>
        ) : (
          'Calculate Quote'
        )}
      </button>

      {hasCalculated && quoteResult && (
        <div className="quote-total">
          <div className="quote-breakdown">
            {quoteResult.lineItems.map((item) => (
              <div className="quote-line" key={`${item.category}-${item.field}`}>
                <span>{item.label} × {item.quantity}</span>
                <span>${item.amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="quote-line quote-line-subtotal">
              <span>Service subtotal</span>
              <span>${quoteResult.serviceSubtotal.toFixed(2)}</span>
            </div>
            {quoteResult.quantityDiscounts > 0 && (
              <div className="quote-line quote-line-discount">
                <span>Quantity discounts</span>
                <span>-${quoteResult.quantityDiscounts.toFixed(2)}</span>
              </div>
            )}
            {quoteResult.orderDiscount > 0 && (
              <div className="quote-line quote-line-discount">
                <span>Order discount ({(quoteResult.orderDiscountRate * 100).toFixed(1)}%)</span>
                <span>-${quoteResult.orderDiscount.toFixed(2)}</span>
              </div>
            )}
          </div>
          <h3>Total Quote: ${quoteResult.total.toFixed(2)}</h3>
          <p className={`quote-status ${quoteResult.meetsMinimum ? 'quote-status-ok' : 'quote-status-warn'}`}>
            {quoteResult.meetsMinimum
              ? `✓ Meets our $${MINIMUM_BOOKING} minimum booking`
              : `Add $${(MINIMUM_BOOKING - quoteResult.total).toFixed(2)} more to reach our $${MINIMUM_BOOKING} minimum booking`}
          </p>
          <p className="quote-disclaimer">
            Quoted prices are estimates only and may change if the actual job differs from the
            original booking, including where payment has already been made.
          </p>
          <button
            className="calculate-btn proceed-btn"
            onClick={backToBook}
            disabled={!quoteResult.meetsMinimum}
            title={!quoteResult.meetsMinimum ? 'Add more services to reach the minimum booking' : undefined}
          >
            Proceed to Booking
          </button>
        </div>
      )}
    </div>
  );

  // Standalone route (reached from a Services card or direct URL): show a matching photo
  // alongside the calculator, same as the embedded Homepage version. Embedded usage
  // (seoEnabled=false) skips this wrapper since the parent page already supplies its own image.
  if (!seoEnabled) {
    return formContent;
  }

  const image = CATEGORY_IMAGES[preselectedCategory] || CATEGORY_IMAGES.carpet;

  return (
    <>
      <div className="quote-page-back-wrap">
        <button type="button" className="quote-back-btn" onClick={backToServices}>
          ← Back to Services
        </button>
      </div>
      <div className="quote-page-layout">
        <div className="quote-page-image">
          <img src={image.src} alt={image.alt} />
        </div>
        <div className="quote-page-panel">{formContent}</div>
      </div>
    </>
  );
};

export default ServiceQuoteForm;
